import useRegisteredModuleFilters from '../../moduleFilters';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSession } from '@/app/providers/session-provider';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { PageActionRow, PageDivider, PageFrame } from '@/shared/components/patterns';
import {
  AssignmentsCreateButton,
  AssignmentDetailDialog,
  AssignmentsFilters,
  AssignmentsHeader,
  AssignmentsStats,
  AssignmentsTabs,
  CreateAssignmentDialog,
  DeleteAssignmentDialog,
  OverdueOperationalView,
  TeamAssignmentsDialog,
} from '@/modules/deals/components/assignments';
import { useAssignmentsData } from '@/modules/deals/hooks/index';
import {
  listExpensesResult,
  listPaymentsWithClientIdsResult,
} from '@/modules/accounting/services';
import { getOverdueItems } from '@/modules/dashboard/utils/dashboard.helpers';
import {
  idsMatch,
  isAssignmentOverdue,
  isAssignmentAssignedToCurrentUser,
  isAssignmentRequestedByCurrentUser,
} from '@/modules/deals/utils';
import '@/modules/deals/styles/assignments.css';

export default function DealsPage() {
  const { capabilities } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [teamDialogTab, setTeamDialogTab] = useState('da_accettare');
  const [accountingOverdueResult, setAccountingOverdueResult] = useState({
    status: 'idle',
    payments: [],
    expenses: [],
    error: '',
  });
  const {
    teamAssignments,
    currentUser,
    staff,
    clients,
    suppliers,
    isLoadingAssignments,
    assignmentsModuleDisabled,
    assignmentsTenantDatabaseBlocked,
    staffUnavailable,
    staffCatalogStatus,
    clientsCatalogStatus,
    suppliersCatalogStatus,
    assignmentCreationWarning,
    assignmentsError,
    assignmentsCatalogStatus,
    isClientFiltered,
    clientFilterLabel,
    assignmentTypes,
    expandedAssignment,
    activeTab,
    setActiveTab,
    personalFilters,
    personalFilterOptions,
    updatePersonalFilter,
    resetPersonalFilters,
    isCreateDialogOpen,
    isEditDialogOpen,
    setIsEditDialogOpen,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    assignmentToDelete,
    newAssignment,
    formErrors,
    commentDrafts,
    assignmentActionStates,
    isSubmittingAssignment,
    incomingAccepted,
    incomingPending,
    completedAssignments,
    filteredMyRequests,
    overdueAssignments,
    toggleExpand,
    openCreateDialog,
    resetCreateDialog,
    updateNewAssignmentField,
    toggleRecipient,
    requestDeleteAssignment,
    confirmDeleteAssignment,
    cancelDeleteAssignment,
    handleAcceptAssignment,
    handleRejectAssignment,
    handleCompleteAssignment,
    openEditDialog,
    assignmentToEdit,
    updateAssignmentInline,
    resetEditDialog,
    handleUpdateAssignment,
    updateCommentDraft,
    publishComment,
    handleCreateAssignment,
  } = useAssignmentsData();
  const moduleFilters = useRegisteredModuleFilters('tasks', teamAssignments, { clientId: clients, supplierId: suppliers });
  const dealsModuleDisabled = assignmentsModuleDisabled;
  const operationalMode = searchParams.get('operational');
  const overdueType = searchParams.get('type') || 'all';
  const showOverdueOperationalView = operationalMode === 'overdue';
  const requestedTeamTab = searchParams.get('scope') === 'team' ? searchParams.get('assignmentTab') : '';
  useEffect(() => {
    if (['da_accettare', 'in_corso', 'completati', 'scadute'].includes(requestedTeamTab)) {
      setTeamDialogTab(requestedTeamTab);
      setTeamDialogOpen(true);
    }
  }, [requestedTeamTab]);
  function changeTeamDialogOpen(open) {
    setTeamDialogOpen(open);
    if (!open && requestedTeamTab) {
      const next = new URLSearchParams(searchParams);
      next.delete('scope'); next.delete('assignmentTab');
      setSearchParams(next, { replace: true });
    }
  }

  const detailAssignmentId = searchParams.get('assignmentId') || '';
  const detailAssignment = useMemo(
    () => teamAssignments.find((assignment) => idsMatch(assignment.id, detailAssignmentId)) || null,
    [detailAssignmentId, teamAssignments]
  );
  const canEditDetailAssignment = Boolean(
    detailAssignment &&
    isAssignmentRequestedByCurrentUser(detailAssignment, currentUser)
  );
  const canCompleteDetailAssignment = Boolean(
    detailAssignment &&
    detailAssignment.status === 'in_corso' &&
    isAssignmentAssignedToCurrentUser(detailAssignment, currentUser)
  );
  const canDeleteDetailAssignment = Boolean(
    detailAssignment &&
    detailAssignment.status === 'da_accettare' &&
    capabilities?.canManageDealDestructiveActions === true &&
    isAssignmentRequestedByCurrentUser(detailAssignment, currentUser)
  );
  const teamStats = {
    pending: teamAssignments.filter((assignment) => assignment.status === 'da_accettare').length,
    inProgress: teamAssignments.filter((assignment) => assignment.status === 'in_corso').length,
    completed: teamAssignments.filter((assignment) => assignment.status === 'completato').length,
    overdue: teamAssignments.filter(isAssignmentOverdue).length,
  };
  const overdueItems = useMemo(() => getOverdueItems({
    assignments: teamAssignments,
    payments: accountingOverdueResult.payments,
    expenses: accountingOverdueResult.expenses,
  }), [accountingOverdueResult.expenses, accountingOverdueResult.payments, teamAssignments]);

  function openTeamDialog(tab) {
    setTeamDialogTab(tab);
    setTeamDialogOpen(true);
  }

  function updateOverdueType(type) {
    const next = new URLSearchParams(searchParams);
    next.set('operational', 'overdue');
    next.set('type', type || 'all');
    setSearchParams(next);
  }

  function openAssignmentDetail(assignmentId, mode = 'view') {
    if (assignmentId === null || assignmentId === undefined || assignmentId === '') return;
    const next = new URLSearchParams(searchParams);
    next.set('assignmentId', String(assignmentId));
    next.set('mode', mode);
    setSearchParams(next);
  }

  function closeAssignmentDetail() {
    const next = new URLSearchParams(searchParams);
    next.delete('assignmentId');
    next.delete('mode');
    setSearchParams(next);
  }

  useEffect(() => {
    if (!showOverdueOperationalView) return undefined;

    let cancelled = false;

    async function loadAccountingOverdueData() {
      const [paymentsResult, expensesResult] = await Promise.all([
        listPaymentsWithClientIdsResult(),
        listExpensesResult(),
      ]);

      if (cancelled) return;

      const failedResult = [paymentsResult, expensesResult].find((result) => !result.ok);
      const tenantBlocked = [paymentsResult.error, expensesResult.error].some((error) =>
        [
          'ACCOUNTING_TENANT_DATABASE_NOT_READY',
          'ACCOUNTING_TENANT_DATABASE_UNAVAILABLE',
          'ACCOUNTING_TENANT_DATABASE_METADATA_MISSING',
        ].includes(error)
      );

      setAccountingOverdueResult({
        status: failedResult
          ? tenantBlocked
            ? 'tenant_database_unavailable'
            : failedResult.error === 'ACCOUNTING_MODULE_DISABLED'
              ? 'module_disabled'
              : failedResult.error === 'ACCOUNTING_AUTH_REQUIRED'
                ? 'auth_required'
                : 'unavailable'
          : 'available',
        payments: paymentsResult.ok ? paymentsResult.data : [],
        expenses: expensesResult.ok ? expensesResult.data : [],
        error: failedResult?.message || failedResult?.error || '',
      });
    }

    loadAccountingOverdueData();

    return () => {
      cancelled = true;
    };
  }, [showOverdueOperationalView]);

  return (
    <PageFrame filterModel={moduleFilters} className="assignments-page">
      {!dealsModuleDisabled ? <AssignmentsHeader /> : null}

      {dealsModuleDisabled ? (
        <Alert className="assignments-alert">
          <AlertDescription>
            {assignmentsError || 'Modulo affari non abilitato per questo workspace.'}
          </AlertDescription>
        </Alert>
      ) : null}

      {!dealsModuleDisabled && assignmentsTenantDatabaseBlocked ? (
        <Alert className="assignments-alert">
          <AlertDescription>
            {assignmentsError || 'Database tenant non disponibile per il modulo affari.'}
          </AlertDescription>
        </Alert>
      ) : null}

      {!dealsModuleDisabled && !assignmentsTenantDatabaseBlocked ? (
        <>
          <section className="assignments-team-section">
            <div className="assignments-team-section__header">
              <h2>Task del team</h2>
              <p>Overview sintetica dei task del team. Clicca una metrica per aprire il dettaglio filtrato.</p>
            </div>
            <AssignmentsStats
              stats={teamStats}
              activeMetric={teamDialogOpen ? teamDialogTab : ''}
              onMetricClick={openTeamDialog}
            />
          </section>
          <PageDivider />
          <AssignmentsFilters
            filters={personalFilters}
            options={personalFilterOptions}
            onChange={updatePersonalFilter}
            onReset={resetPersonalFilters}
          />
          <PageDivider />
          <PageActionRow>
            <AssignmentsCreateButton onCreate={openCreateDialog} />
          </PageActionRow>
        </>
      ) : null}

      {!dealsModuleDisabled && staffUnavailable ? (
        <Alert className="assignments-alert">
          <AlertDescription>
            Catalogo staff non disponibile dal backend. Le assegnazioni usano un catalogo vuoto finché il servizio non torna disponibile.
          </AlertDescription>
        </Alert>
      ) : null}

      {!dealsModuleDisabled && !assignmentsTenantDatabaseBlocked ? (
        <section className="assignments-mine-section">
          {showOverdueOperationalView ? (
            <>
              <OverdueOperationalView
                items={overdueItems}
                activeType={overdueType}
                onTypeChange={updateOverdueType}
                accountingStatus={accountingOverdueResult.status}
                accountingError={accountingOverdueResult.error}
                onOpenTask={openAssignmentDetail}
              />
              <PageDivider />
            </>
          ) : null}
          <div className="assignments-section-card__header">
            <div>
              <h2 className="assignments-section-card__title">I miei task</h2>
              <p className="assignments-section-card__description">
                Task personali ordinati per stato operativo
              </p>
            </div>
          </div>

          <AssignmentsTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            myRequests={moduleFilters.filterRows(filteredMyRequests)}
            incomingAccepted={moduleFilters.filterRows(incomingAccepted)}
            incomingPending={moduleFilters.filterRows(incomingPending)}
            completedAssignments={moduleFilters.filterRows(completedAssignments)}
            overdueAssignments={moduleFilters.filterRows(overdueAssignments)}
            isClientFiltered={isClientFiltered}
            clientFilterLabel={clientFilterLabel}
            currentUser={currentUser}
            staff={staff}
            clients={clients}
            clientsCatalogStatus={clientsCatalogStatus}
            expandedAssignment={expandedAssignment}
            commentDrafts={commentDrafts}
            assignmentActionStates={assignmentActionStates}
            canDelete={capabilities?.canManageDealDestructiveActions === true}
            onToggleExpand={toggleExpand}
            onDelete={requestDeleteAssignment}
            onAccept={handleAcceptAssignment}
            onReject={handleRejectAssignment}
            onComplete={handleCompleteAssignment}
            onEdit={openEditDialog}
            onCommentDraftChange={updateCommentDraft}
            onPublishComment={publishComment}
            onOpenCreate={openCreateDialog}
            onOpenDetail={openAssignmentDetail}
          />
        </section>
      ) : null}

      {!dealsModuleDisabled && !assignmentsTenantDatabaseBlocked ? (
        <TeamAssignmentsDialog
          open={teamDialogOpen}
          onOpenChange={changeTeamDialogOpen}
          activeTab={teamDialogTab}
          onTabChange={setTeamDialogTab}
          assignments={moduleFilters.filterRows(teamAssignments)}
          currentUser={currentUser}
          staff={staff}
          clients={clients}
          suppliers={suppliers}
          staff={staff}
          clientsCatalogStatus={clientsCatalogStatus}
          commentDrafts={commentDrafts}
          assignmentActionStates={assignmentActionStates}
          onAccept={handleAcceptAssignment}
          onReject={handleRejectAssignment}
          onCommentDraftChange={updateCommentDraft}
          onPublishComment={publishComment}
          onOpenDetail={(assignmentId) => {
            setTeamDialogOpen(false);
            openAssignmentDetail(assignmentId);
          }}
        />
      ) : null}

      {!dealsModuleDisabled && !assignmentsTenantDatabaseBlocked && detailAssignmentId ? (
        <AssignmentDetailDialog
          open
          onOpenChange={(open) => {
            if (!open) closeAssignmentDetail();
          }}
          focusComments={searchParams.get('mode') === 'comments'}
          assignmentId={detailAssignmentId}
          assignment={detailAssignment}
          currentUser={currentUser}
          staff={staff}
          clients={clients}
          suppliers={suppliers}
          actionState={detailAssignment ? assignmentActionStates[detailAssignment.id] || {} : {}}
          isLoading={isLoadingAssignments}
          loadError={!isLoadingAssignments && assignmentsCatalogStatus !== 'available' && assignmentsCatalogStatus !== 'empty'
            ? assignmentsError || 'Impossibile caricare il task.'
            : ''}
          canDelete={canDeleteDetailAssignment}
          canComplete={canCompleteDetailAssignment}
          onDelete={(assignment) => {
            closeAssignmentDetail();
            requestDeleteAssignment(assignment);
          }}
          onAccept={handleAcceptAssignment}
          onReject={handleRejectAssignment}
          onComplete={handleCompleteAssignment}
          onSave={canEditDetailAssignment ? updateAssignmentInline : undefined}
        />
      ) : null}

      {!dealsModuleDisabled && !assignmentsTenantDatabaseBlocked ? (
        <CreateAssignmentDialog
          open={isCreateDialogOpen}
          onOpenChange={(open) => (open ? openCreateDialog() : resetCreateDialog())}
          staff={staff}
          staffCatalogStatus={staffCatalogStatus}
          clients={clients}
          clientsCatalogStatus={clientsCatalogStatus}
          suppliers={suppliers}
          suppliersCatalogStatus={suppliersCatalogStatus}
          creationWarning={assignmentCreationWarning}
          currentUser={currentUser}
          assignmentTypes={assignmentTypes}
          newAssignment={newAssignment}
          formErrors={formErrors}
          onFieldChange={updateNewAssignmentField}
          onToggleRecipient={toggleRecipient}
          onSubmit={handleCreateAssignment}
          isSubmitting={isSubmittingAssignment}
        />
      ) : null}

      {!dealsModuleDisabled && !assignmentsTenantDatabaseBlocked ? (
        <CreateAssignmentDialog
          open={isEditDialogOpen}
          onOpenChange={(open) => (open ? setIsEditDialogOpen(true) : resetEditDialog())}
          mode="edit"
          assignment={assignmentToEdit}
          staff={staff}
          staffCatalogStatus={staffCatalogStatus}
          clients={clients}
          clientsCatalogStatus={clientsCatalogStatus}
          suppliers={suppliers}
          suppliersCatalogStatus={suppliersCatalogStatus}
          currentUser={currentUser}
          assignmentTypes={assignmentTypes}
          newAssignment={newAssignment}
          formErrors={formErrors}
          onFieldChange={updateNewAssignmentField}
          onToggleRecipient={toggleRecipient}
          onSubmit={handleUpdateAssignment}
          isSubmitting={isSubmittingAssignment}
        />
      ) : null}

      {!dealsModuleDisabled && !assignmentsTenantDatabaseBlocked ? (
        <DeleteAssignmentDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          assignment={assignmentToDelete}
          onConfirm={confirmDeleteAssignment}
          onCancel={cancelDeleteAssignment}
        />
      ) : null}
    </PageFrame>
  );
}
